import { getPlatformTag } from '@apparel/core/platform';
import { Col, ColProps } from '@components/Layout/Col';
import { Row } from '@components/Layout/Row';
import { I18n } from '@components/Trans';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { uuid } from '@lib/random';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import { Category, CategoryType } from '@model/Category';
import { useApi } from '@model/utils/useApi';
import { call } from '@packages/functions';
import React, { useEffect, useRef, useState } from 'react';
import { findDOMNode } from 'react-dom';
import CategorySelectableCard from './CategorySelectableCard';

interface CategorySelectorProps {
    selectedId?: number;
    categories?: CategoryType[];
    onSelect?: (data: any, list: any[]) => void;
    onLoad?: (data: any, list: any[]) => void;
    colProps?: ColProps;
    // Auto-fetch categories
    auto?: boolean;
    // Display as select box
    select?: boolean;
    // Select box placeholder value
    label?: string;
    wholesalerId?: number;
}

const CategorySelector = (props: CategorySelectorProps) => {
    const { categories, select, auto, label, onLoad, selectedId: defaultValue, ...rest } = props;

    const inputRef = useRef(null);
    const [selectedId, setSelectedId] = useState(select ? defaultValue || 'none' : defaultValue);

    const isMobile = useMediaQuery('mobile');

    const colProps = {
        xs: 6,
        sm: 6,
        md: 4,
        lg: 3,
        padding: isMobile ? 6 : 14,
        // $FlowFixMe
        ...(props.colProps || {}),
    };

    let categories$ = categories;

    const { loading, data = {} } = useApi<any>({
        model: Category,
        onMount: {
            cache: 'categories_index_list',
            method: 'all',
            params: {
                fields: ['id', 'name', ['image', ['id', 'thumbnail_path']]],
                order_by: 'name',
                order_direction: 'asc',

                tags: getPlatformTag(),
                wholesaler_id: rest?.wholesalerId
            },

            after: (data, res) => {
                if (data && data.items) {
                    call(onLoad, data.items, res);
                }
            },
        },
    });

    useEffect(() => {
        if (defaultValue && !selectedId) {
            setSelectedId(defaultValue);
        }
    }, [defaultValue]);

    if (!loading && auto && !categories$ && data) {
        categories$ = data.items;
    }

    const handleOnSelect = (id, title) => {
        if (id === 'none') {
            id = null;
        }

        if (id && id.target) {
            id = id.target.value;
        }

        setSelectedId(id);
        call(props.onSelect, id, title, categories$);
    };

    if (select) {
        const id = `categories_${uuid()}`;
        // eslint-disable-next-line react/no-find-dom-node
        const el: any = inputRef.current ? findDOMNode(inputRef.current) : null;

        return (
            <FormControl variant="outlined" {...(rest as any)}>
                <InputLabel ref={inputRef} htmlFor={id}>
                    {label}
                </InputLabel>
                <Select
                    value={selectedId}
                    onChange={handleOnSelect}
                    input={
                        <OutlinedInput
                            labelWidth={(el ? el.offsetWidth : 175) + 5}
                            id={id}
                            margin={'dense'}
                            endAdornment={
                                loading ? (
                                    <span>
                                        <CircularProgress color={'primary'} size={18} />
                                    </span>
                                ) : null
                            }
                        />
                    }
                >
                    <MenuItem value={'none'}>None</MenuItem>
                    {(categories$ || []).map(({ id, name }) => (
                        <MenuItem selected={selectedId === id} value={id} key={`${id}_${name}`}>
                            {name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    if (auto && loading) {
        return (
            <Col xs padding={14} align={'center'} justify={'center'}>
                <CircularProgress color={'primary'} size={18} />
                <I18n color={'textSecondary'} style={{ marginLeft: 8 }}>
                    Loading...
                </I18n>
            </Col>
        );
    }

    if (!categories$) {
        return null;
    }

    return (
        <Row>
            {categories$.map((category) => {
                return (
                    <Col key={category.id} {...(colProps as any)}>
                        <CategorySelectableCard
                            category={category}
                            onSelect={handleOnSelect}
                            isActive={category.id === selectedId}
                        />
                    </Col>
                );
            })}
        </Row>
    );
};

export default CategorySelector;
